import cx from "clsx";
import ButtonSecondary from "kreate-common/modules/kreate-ui/components/ButtonSecondary";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { CLIENT_ENV } from "kreate-env/client";
import Image from "next/image";
import { useRouter } from "next/router";
import * as React from "react";

import pngBackground from "./assets/background.png";
import pngGoogle from "./assets/google.png";
import pngWallet from "./assets/wallet.png";
import LogoKreate from "./components/LogoKreate";
import styles from "./index.module.scss";
import {
  authenticateByCardanoWallet,
  getUserFromCardanoWallet,
  safelyGoToRedirectedUrl,
} from "./utils";

import { useBusyCount } from "@/modules/common-hooks/useBusyCount";
import { shamelesslyRevalidateEverything } from "@/modules/common-utils/shamelesslyRevalidateEverything";
import { useGoogleAuthentication } from "@/modules/google-authentication";
import KreateHead from "@/modules/kreate-components/components/KreateHead";
import { useModalTrigger$ModalConfirm } from "@/modules/kreate-modals/modals/ModalConfirm/hooks";
import { useModalTrigger$ModalConnectWallet } from "@/modules/kreate-modals/modals/ModalConnectWallet/hooks/useModalTrigger$ModalConnectWallet";
import { useModalTrigger$ModalFailure } from "@/modules/kreate-modals/modals/ModalFailure/hooks";

const Button = ButtonSecondary.withDefaultProps({
  size: "xl",
  color: "inherit",
  className: styles.button,
});

type Props = {
  className?: string;
  style?: React.CSSProperties;
};

export default function PageLogin({ className, style }: Props) {
  const router = useRouter();
  const modalTriggers = {
    ModalConfirm: useModalTrigger$ModalConfirm(),
    ModalConnectWallet: useModalTrigger$ModalConnectWallet(),
    ModalFailure: useModalTrigger$ModalFailure(),
  };

  const handleError = async (error: unknown) => {
    await modalTriggers.ModalFailure.openModalWithError(error);
  };

  const googleAuthenticationApi = useGoogleAuthentication();
  const { busy, withCustomErrorHandler } = useBusyCount();

  const authenticateByWallet = withCustomErrorHandler(handleError)(async () => {
    const cip30Wallet =
      await modalTriggers.ModalConnectWallet.getCip30WalletOrOpenModal();

    const user = await getUserFromCardanoWallet(cip30Wallet);
    if (!user) {
      const confirmed = await modalTriggers.ModalConfirm.openModal(
        <div>
          <span>{"By continuing to Login with your Cardano Wallet, you"}</span>
          <span>{" will be creating a new Kreate Profile. Is this what"}</span>
          <span>
            {" you want to do? If not, please log into your existing"}
          </span>
          <span>{" Profile first and click "}</span>
          <strong>{"[Edit Profile]"}</strong>
          <span>{" to Link your wallet."}</span>
        </div>,
        {
          labelButtonAccept: "Create a New Profile",
          title: "This address isn't linked to any Profile.",
        }
      );
      if (!confirmed) {
        return;
      }
    }
    const { userId } = await authenticateByCardanoWallet(cip30Wallet);
    console.log(`logged in as ${userId}`);
    await shamelesslyRevalidateEverything();
    await safelyGoToRedirectedUrl(router);
  });

  return (
    <div className={cx(styles.container, className)}>
      <KreateHead
        title="Kreate - Web3 Art Community"
        description={
          "Kreate.Art is an All-in-One Art-focused platform. Share, " +
          "publish, sell, auction, and trade your art here with Art " +
          "lovers all over the world! We have a supportive community " +
          "aimed at fostering sustainability, social connections, " +
          "and creative growth. 🌱"
        }
        imageUrl={undefined}
        url={`${CLIENT_ENV.KREATE_ORIGIN}/auth/login`}
      />
      <Image src={pngBackground} alt="" fill style={{ objectFit: "cover" }} />
      <Flex.Row style={style} minHeight="100vh">
        <Flex.Cell flex="2 2 0" className={styles.leftCol} />
        <Flex.Col
          flex="1 1 0"
          padding="48px 60px"
          alignItems="center"
          justifyContent="space-between"
          className={styles.rightCol}
        >
          <LogoKreate />
          <Flex.Col gap="16px" alignItems="stretch" minWidth="450px">
            <Flex.Row className={styles.title} justifyContent="center">
              {"Login"}
            </Flex.Row>
            <Button
              icon={<Image src={pngGoogle} alt="" width={24} height={24} />}
              onClick={() => googleAuthenticationApi?.triggerAuthentication()}
              disabled={busy}
            >
              <span>{"Continue with Google"}</span>
            </Button>
            <Flex.Row alignItems="center" gap="8px" padding="8px 0">
              <Flex.Cell className={styles.line} flex="1 1 0"></Flex.Cell>
              <Flex.Cell className={styles.textOr} flex="0 0 auto">
                {"Or"}
              </Flex.Cell>
              <Flex.Cell className={styles.line} flex="1 1 0"></Flex.Cell>
            </Flex.Row>
            <Button
              icon={<Image src={pngWallet} alt="" width={24} height={24} />}
              onClick={authenticateByWallet}
              disabled={busy}
            >
              <span>{"Continue with Cardano Wallet"}</span>
            </Button>
          </Flex.Col>
          <div className={styles.text}>© 2023 Kreate</div>
        </Flex.Col>
      </Flex.Row>
    </div>
  );
}
